/**
 * useAuth Hook
 *
 * Authentication hook and context provider for managing user authentication state.
 * Provides login, logout, register, and user state management.
 *
 * Usage:
 *   const { user, login, logout, loading } = useAuth();
 */

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { apiClient } from '@/services/api';

interface User {
  id: string;
  name: string; // Maps to full_name from backend
  email: string;
  full_name?: string; // Direct backend field
  company?: string;
  phone?: string;
  avatar?: string; // Maps to avatar_url from backend
  avatar_url?: string; // Direct backend field
  role?: string;
  status?: string;
  organization_id?: string;
  organization_currency?: string; // Currency code of the current org (e.g., USD, INR)
  organization_max_campaign_budget?: number | null; // Max campaign budget for LLM proposals
  subscription?: {
    plan: string;
    status: string;
  };
}

interface Organization {
  id: string;
  name: string;
  slug: string;
  industry?: string;
  role?: string;
  currency?: string;
  max_campaign_budget?: number | null;
  website_url?: string | null;
  phone?: string | null;
  target_countries?: string[] | null;
}

interface SubscriptionInfo {
  id: string;
  plan: string;
  status: string;
  trial_ends?: string;
  limits_initialized?: number;
}

interface RegisterResponse {
  user: User;
  organization: Organization;
  subscription: SubscriptionInfo;
}

interface AuthContextType {
  user: User | null;
  currentOrganization: Organization | null;
  loading: boolean;
  error: string | null;
  login: (email: string, password: string, rememberMe?: boolean) => Promise<void>;
  register: (data: RegisterData) => Promise<RegisterResponse>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  switchOrganization: (orgId: string) => Promise<void>;
}

interface RegisterData {
  name: string;
  email: string;
  password: string;
  company?: string;
  accountType?: string;
  termsAccepted?: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [currentOrganization, setCurrentOrganization] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Normalize backend user response to frontend User interface
  const normalizeUser = (backendUser: any): User => {
    return {
      ...backendUser,
      name: backendUser.full_name || backendUser.name || backendUser.email,
      avatar: backendUser.avatar_url || backendUser.avatar,
    };
  };

  // Check for existing session on mount
  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');

      if (!token) {
        setLoading(false);
        return;
      }

      // Verify token and get user data
      const response = await apiClient.get('/api/v1/auth/me');
      const userData = normalizeUser(response.data);
      setUser(userData);
      setError(null);

      // Load current organization based on organization_id from token
      if (userData.organization_id) {
        try {
          const orgsResponse = await apiClient.get('/api/v1/organizations');
          const organizations: Organization[] = orgsResponse.data;

          // Find the organization matching the user's current organization_id
          const currentOrg = organizations.find(
            (org) => org.id === userData.organization_id
          );

          if (currentOrg) {
            setCurrentOrganization(currentOrg);
          } else if (organizations.length > 0) {
            // Fallback: use first organization if exact match not found
            console.warn(
              `Organization ${userData.organization_id} not found in user's organizations, using first available`,
              organizations[0]
            );
            setCurrentOrganization(organizations[0]);
          }
        } catch (orgErr) {
          console.warn('Failed to load organizations during auth check:', orgErr);
          // Continue without organization context - don't fail auth
          setCurrentOrganization(null);
        }
      }
    } catch (err: any) {
      console.error('Auth check failed:', err);
      // Clear invalid token
      localStorage.removeItem('auth_token');
      sessionStorage.removeItem('auth_token');
      setUser(null);
      setCurrentOrganization(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email: string, password: string, rememberMe = false) => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiClient.post('/api/v1/auth/login', {
        email,
        password,
      });

      const { access_token, refresh_token, user: userData } = response.data;

      // Store tokens based on remember me preference
      if (rememberMe) {
        localStorage.setItem('auth_token', access_token);
        if (refresh_token) localStorage.setItem('refresh_token', refresh_token);
      } else {
        sessionStorage.setItem('auth_token', access_token);
        if (refresh_token) sessionStorage.setItem('refresh_token', refresh_token);
      }

      const normalizedUser = normalizeUser(userData);
      setUser(normalizedUser);

      // Load current organization after login
      if (normalizedUser.organization_id) {
        try {
          const orgsResponse = await apiClient.get('/api/v1/organizations');
          const organizations: Organization[] = orgsResponse.data;

          const currentOrg = organizations.find(
            (org) => org.id === normalizedUser.organization_id
          );

          if (currentOrg) {
            setCurrentOrganization(currentOrg);
          } else if (organizations.length > 0) {
            setCurrentOrganization(organizations[0]);
          }
        } catch (orgErr) {
          console.warn('Failed to load organizations during login:', orgErr);
          setCurrentOrganization(null);
        }
      }

      // Warm up backend services in parallel (fire-and-forget)
      // Wakes cold-start Cloud Run instances so they're ready when the dashboard loads
      apiClient.get('/api/v1/my-subscription').catch(() => {});
      apiClient.get('/api/v1/oauth/connections/all-organizations').catch(() => {});
      apiClient.get('/api/v1/analytics/dashboard?time_range=7d').catch(() => {});
      apiClient.get('/api/v1/conversations?limit=1&offset=0').catch(() => {});
    } catch (err: any) {
      console.error('Login failed:', err);

      // Extract detailed error message from various response formats
      let errorMessage = 'Login failed';

      if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err.response?.status === 401) {
        errorMessage = 'Invalid email or password. Please try again.';
      } else if (err.response?.status === 429) {
        errorMessage = 'Too many login attempts. Please try again later.';
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const register = async (data: RegisterData): Promise<RegisterResponse> => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiClient.post('/api/v1/auth/register', {
        full_name: data.name,
        email: data.email,
        password: data.password,
        company: data.company || undefined,
        account_type: data.accountType || undefined,
        terms_accepted: data.termsAccepted || false,
      });

      // Email registered but not yet verified — caller handles the redirect
      if (response.data?.code === 'VERIFICATION_PENDING') {
        return response.data as any;
      }

      const { user: userData, organization, subscription } = response.data;

      // NOTE: Registration does NOT authenticate the user
      // Registration only creates the account; it does NOT set user state or JWT token
      // User must login separately after completing their chosen path:
      // - For paid plans (pro/enterprise): user goes to checkout first, then logs in
      // - For free plans (basic): user goes to login page and logs in
      //
      // We do NOT call setUser() here to prevent the PublicRoute from auto-redirecting
      // to dashboard. The user must explicitly login to be authenticated.

      // Return the full response so RegisterPage can make routing decisions
      // Just return userData directly - normalizeUser is already done by backend
      return {
        user: {
          ...userData,
          name: userData.full_name || userData.name || userData.email,
          avatar: userData.avatar_url || userData.avatar,
        } as User,
        organization: organization,
        subscription: subscription,
      };
    } catch (err: any) {
      console.error('Registration failed:', err);

      // Extract detailed error message from various response formats
      let errorMessage = 'Registration failed';

      // Try to get error details from different response structures
      if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err.response?.status === 409) {
        // Conflict — email belongs to an already-verified account
        errorMessage = 'An account with this email already exists. Please log in.';
      } else if (err.response?.status === 400) {
        // Bad request - might have validation errors
        errorMessage = err.response?.data?.message || 'Invalid registration data. Please check your input.';
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      setLoading(true);

      // Call logout endpoint
      await apiClient.post('/api/v1/auth/logout');
    } catch (err) {
      console.error('Logout error:', err);
      // Continue with logout even if API call fails
    } finally {
      // Clear tokens and user state
      localStorage.removeItem('auth_token');
      localStorage.removeItem('refresh_token');
      sessionStorage.removeItem('auth_token');
      sessionStorage.removeItem('refresh_token');
      setUser(null);
      setCurrentOrganization(null);
      setError(null);
      setLoading(false);

      // Redirect to login
      window.location.href = '/login';
    }
  };

  const refreshUser = async () => {
    try {
      const response = await apiClient.get('/api/v1/auth/me');
      const userData = normalizeUser(response.data);
      setUser(userData);

      // Also refresh organization context if organization_id is present
      if (userData.organization_id) {
        try {
          const orgsResponse = await apiClient.get('/api/v1/organizations');
          const organizations: Organization[] = orgsResponse.data;

          const currentOrg = organizations.find(
            (org) => org.id === userData.organization_id
          );

          if (currentOrg) {
            setCurrentOrganization(currentOrg);
          } else if (organizations.length > 0) {
            setCurrentOrganization(organizations[0]);
          }
        } catch (orgErr) {
          console.warn('Failed to refresh organizations:', orgErr);
          setCurrentOrganization(null);
        }
      }
    } catch (err) {
      console.error('Failed to refresh user:', err);
      // If refresh fails, user might be logged out
      setUser(null);
      setCurrentOrganization(null);
    }
  };

  const switchOrganization = async (orgId: string) => {
    try {
      // NOTE: Do NOT set global loading=true here — it triggers a fullscreen spinner
      // that blocks the entire app. Org switching should be seamless.
      setError(null);

      // Call the switch organization endpoint to get a new token
      const response = await apiClient.post('/api/v1/organizations/switch', {
        organization_id: orgId,
      });

      const { access_token, user: userData } = response.data;

      // Update token in storage (preserve storage type)
      // IMPORTANT: Update token FIRST so subsequent API calls use new org context
      const useLocalStorage = !!localStorage.getItem('auth_token');
      if (useLocalStorage) {
        localStorage.setItem('auth_token', access_token);
      } else {
        sessionStorage.setItem('auth_token', access_token);
      }

      // Update user state
      setUser(normalizeUser(userData));

      // Fetch full organization data (with currency, max_campaign_budget, role)
      // The switch endpoint doesn't return the full org object
      let organization: Organization | null = null;
      try {
        const orgsResponse = await apiClient.get('/api/v1/organizations');
        const organizations = orgsResponse.data;
        organization = organizations.find((org: Organization) => org.id === orgId) || null;
      } catch (orgErr) {
        console.warn('Failed to fetch organizations after switch:', orgErr);
      }
      setCurrentOrganization(organization);

      // Emit event for components that need to refresh their data
      // Include full organization data in the event so listeners can use it
      window.dispatchEvent(
        new CustomEvent('organizationChanged', {
          detail: {
            organizationId: orgId,
            organization: organization,
          },
        })
      );
    } catch (err: any) {
      console.error('Failed to switch organization:', err);
      const errorMessage = err.response?.data?.message || 'Failed to switch organization';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  /**
   * Ensure organization context is synchronized after JWT changes
   * This is a helper function for components that need to guarantee context is fresh
   * Useful after org switches or when context might be out of sync
   */
  const ensureOrgContextRefresh = async () => {
    try {
      // Re-fetch user data with current token (which should have the correct org_id)
      const response = await apiClient.get('/api/v1/auth/me');
      const userData = normalizeUser(response.data);
      setUser(userData);

      // Load organization from list based on user's current organization_id
      if (userData.organization_id) {
        try {
          const orgsResponse = await apiClient.get('/api/v1/organizations');
          const organizations: Organization[] = orgsResponse.data;

          const currentOrg = organizations.find(
            (org) => org.id === userData.organization_id
          );

          if (currentOrg) {
            setCurrentOrganization(currentOrg);
          } else if (organizations.length > 0) {
            console.warn(
              `Organization ${userData.organization_id} not found, using first available`,
              organizations[0]
            );
            setCurrentOrganization(organizations[0]);
          }
        } catch (orgErr) {
          console.warn('Failed to refresh organizations context:', orgErr);
          setCurrentOrganization(null);
        }
      }
    } catch (err) {
      console.error('Failed to ensure org context refresh:', err);
      // Don't throw - just warn
    }
  };

  const value: AuthContextType = {
    user,
    currentOrganization,
    loading,
    error,
    login,
    register,
    logout,
    refreshUser,
    switchOrganization,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default useAuth;
